import React, { useEffect, useRef, useState } from "react"
import { Button, DropdownItem } from "reactstrap"
import { useToasts } from "react-toast-notifications"
import { get, post } from "helpers/api_helper"

const failureMessage = (error, subject) => {
  const code = error?.response?.data?.message || error?.message
  const messages = {
    IMPERSONATE_ACCOUNT_PENDING: `Impersonate is unavailable because ${subject} has not been activated.`,
    IMPERSONATE_ACCOUNT_INACTIVE: `Impersonate is unavailable because ${subject} is blocked or inactive.`,
    IMPERSONATE_NOT_ORGANIZER: `Impersonate is unavailable because ${subject} is not an Organizer.`,
    IMPERSONATE_OS_ACCESS_DISABLED: `Impersonate is unavailable because SportyaOS access is disabled for ${subject}.`,
    IMPERSONATE_NO_ASSIGNED_CLUB: `Impersonate is unavailable because ${subject} has no assigned club.`,
    NO_ACCESS: `Impersonate is unavailable because ${subject} does not have SportyaOS access.`,
  }
  return messages[code] || (error?.code === "ECONNABORTED"
    ? "The SportyaOS request timed out. Please try again."
    : "SportyaOS impersonation could not be confirmed. Please try again.")
}

export default function OsImpersonateAction({ account, clubOwner = false, dropdown = false }) {
  const { addToast } = useToasts()
  const [pending, setPending] = useState(false)
  const [confirmedDestination, setConfirmedDestination] = useState(null)
  const running = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])
  const impersonate = async () => {
    if (running.current) return
    running.current = true
    setConfirmedDestination(null)
    let popup, destination
    const showError = message => addToast(message, { appearance: "error", autoDismiss: true })
    try {
      if (!account?.email || !(account.id || account._id)) {
        showError("The target account details are unavailable. Reload the page and try again.")
        return
      }
      const os = new URL(process.env.REACT_APP_OS_URL)
      if (!["http:", "https:"].includes(os.protocol)) throw new Error("Invalid OS URL")
      popup = window.open("", "_blank")
      if (!popup) {
        showError("Allow pop-ups for Control Panel, then try Impersonate again.")
        return
      }
      popup.opener = null
      setPending(true)
      const { data: issuer } = await get("/admin/accounts/auth", { timeout: 15000 })
      if (!issuer?.userId || !issuer?.impersonateKey) throw new Error("Missing session")
      const body = new URLSearchParams({ hash: issuer.impersonateKey, userId: issuer.userId,
        email: account.email, targetAccountId: account.id || account._id }).toString()
      const response = await post(new URL("/api/auth", os.origin).toString(), body, {
        timeout: 20000, headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      const target = response?.user
      if (response?.succeeded !== true || !target?.isImpersonate || !["player", "club"].includes(target.role)
        || String(target.id) !== String(account.id || account._id)) {
        throw new Error(response?.message || "Session not confirmed")
      }
      destination = new URL("/", os.origin).toString()
      if (popup.closed) throw new Error("Tab closed")
      popup.location.replace(destination)
    } catch (error) {
      try { popup?.close() } catch { /* A detached tab must not hide the outcome. */ }
      if (destination) {
        if (mounted.current) setConfirmedDestination(destination)
        addToast("The SportyaOS session is confirmed, but its tab could not be opened. Use Open SportyaOS to continue.", { appearance: "warning", autoDismiss: true })
      } else showError(failureMessage(error, clubOwner ? "the club owner's account" : "this account"))
    } finally {
      running.current = false
      if (mounted.current) setPending(false)
    }
  }
  const props = confirmedDestination
    ? { tag: "a", href: confirmedDestination, target: "_blank", rel: "noopener noreferrer" }
    : { onClick: impersonate, disabled: pending }
  const label = confirmedDestination ? "Open SportyaOS" : pending ? "Opening session…" : "Impersonate"
  return dropdown ? <DropdownItem {...props} toggle={false}>{label}</DropdownItem>
    : <Button {...props} type={confirmedDestination ? undefined : "button"} color="secondary">{label}</Button>
}
